import { DeleteOutlined, EditOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Col, Descriptions, Form, Input, InputNumber, Popconfirm, Row, Table } from 'antd';
import Column from 'antd/es/table/Column';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { ContentHeader } from '../../components';
import ModalTextForm from '../../components/modal/ModalTextForm';
import { formatVND, validateMessages } from '../../helpers/common';
import { QuyLuongInfo } from '../../models/luong/QuyLuong';
import { layDanhSachQuyLuong, luuQuyLuong, xoaQuyLuong } from '../../services/quyluong';
import { hideLoadingScreen, showLoadingScreen } from '../../store/reducers/loading';

const THANG_LABELS = [
    'Tháng 1', 'Tháng 2', 'Tháng 3', 'Tháng 4',
    'Tháng 5', 'Tháng 6', 'Tháng 7', 'Tháng 8',
    'Tháng 9', 'Tháng 10', 'Tháng 11', 'Tháng 12',
];

const ChiTietThang = ({ record }: { record: QuyLuongInfo }) => (
    <div style={{ padding: '8px 24px 12px' }}>
        <Descriptions
            title="Quỹ lương từng tháng"
            bordered
            size="small"
            column={6}
            labelStyle={{ fontWeight: 600, whiteSpace: 'nowrap', width: 80 }}
            contentStyle={{ textAlign: 'right', minWidth: 120 }}
        >
            {THANG_LABELS.map((label, idx) => (
                <Descriptions.Item label={label} key={idx}>
                    {formatVND((record as any)[`quyLuongThang${idx + 1}`] ?? 0)}
                </Descriptions.Item>
            ))}
        </Descriptions>
    </div>
);

const QuanLyQuyLuong = () => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [dataDs, setDataDs] = useState<QuyLuongInfo[]>([]);
    const [openModal, setOpenModal] = useState(false);
    const [editItem, setEditItem] = useState<QuyLuongInfo | null>(null);

    const layDuLieu = async () => {
        setLoading(true);
        try {
            const rs = await layDanhSachQuyLuong();
            setDataDs(rs);
        } catch (err: any) {
            toast.error(err?.toString().replace('Error: ', ''));
        }
        setLoading(false);
    };

    useEffect(() => {
        layDuLieu();
    }, []);

    const onMoModalThem = () => {
        setEditItem(null);
        form.resetFields();
        form.setFieldsValue({ nam: new Date().getFullYear() });
        setOpenModal(true);
    };

    const onMoModalSua = (item: QuyLuongInfo) => {
        setEditItem(item);
        form.setFieldsValue({
            nam: item.nam,
            tongQuyLuong: item.tongQuyLuong,
            quyLuongThang1: item.quyLuongThang1,
            quyLuongThang2: item.quyLuongThang2,
            quyLuongThang3: item.quyLuongThang3,
            quyLuongThang4: item.quyLuongThang4,
            quyLuongThang5: item.quyLuongThang5,
            quyLuongThang6: item.quyLuongThang6,
            quyLuongThang7: item.quyLuongThang7,
            quyLuongThang8: item.quyLuongThang8,
            quyLuongThang9: item.quyLuongThang9,
            quyLuongThang10: item.quyLuongThang10,
            quyLuongThang11: item.quyLuongThang11,
            quyLuongThang12: item.quyLuongThang12,
            ghiChu: item.ghiChu,
        });
        setOpenModal(true);
    };

    const onFinishLuu = async (values: any) => {
        dispatch(showLoadingScreen());
        try {
            const request = {
                id: editItem?.id ?? 0,
                nam: Number(values.nam),
                tongQuyLuong: Number(values.tongQuyLuong ?? 0),
                quyLuongThang1: Number(values.quyLuongThang1 ?? 0),
                quyLuongThang2: Number(values.quyLuongThang2 ?? 0),
                quyLuongThang3: Number(values.quyLuongThang3 ?? 0),
                quyLuongThang4: Number(values.quyLuongThang4 ?? 0),
                quyLuongThang5: Number(values.quyLuongThang5 ?? 0),
                quyLuongThang6: Number(values.quyLuongThang6 ?? 0),
                quyLuongThang7: Number(values.quyLuongThang7 ?? 0),
                quyLuongThang8: Number(values.quyLuongThang8 ?? 0),
                quyLuongThang9: Number(values.quyLuongThang9 ?? 0),
                quyLuongThang10: Number(values.quyLuongThang10 ?? 0),
                quyLuongThang11: Number(values.quyLuongThang11 ?? 0),
                quyLuongThang12: Number(values.quyLuongThang12 ?? 0),
                ghiChu: values.ghiChu ?? '',
            };
            const rs = await luuQuyLuong(request);
            if (rs > 0) {
                toast.success('Lưu quỹ lương thành công!');
                setOpenModal(false);
                layDuLieu();
            } else {
                toast.error('Lưu quỹ lương thất bại!');
            }
        } catch (err: any) {
            toast.error(err?.toString().replace('Error: ', ''));
        }
        dispatch(hideLoadingScreen());
    };

    const onXoa = async (id: number) => {
        dispatch(showLoadingScreen());
        try {
            await xoaQuyLuong(id);
            toast.success('Xóa quỹ lương thành công!');
            layDuLieu();
        } catch (err: any) {
            toast.error(err?.toString().replace('Error: ', ''));
        }
        dispatch(hideLoadingScreen());
    };

    return (
        <section>
            {openModal && (
                <ModalTextForm
                    title={editItem ? `Sửa quỹ lương năm ${editItem.nam}` : 'Thêm quỹ lương'}
                    hideFooter
                    show={openModal}
                    handleClose={() => setOpenModal(false)}
                    size="lg"
                >
                    <Form
                        layout="vertical"
                        validateMessages={validateMessages}
                        onFinish={onFinishLuu}
                        form={form}
                    >
                        <Row gutter={8}>
                            <Col span={8}>
                                <Form.Item rules={[{ required: true }]} label="Năm" name="nam">
                                    <InputNumber
                                        min={2000}
                                        max={2100}
                                        precision={0}
                                        placeholder="Nhập năm"
                                        style={{ width: '100%' }}
                                        disabled={!!editItem}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={16}>
                                <Form.Item rules={[{ required: true }]} label="Tổng quỹ lương năm (đồng)" name="tongQuyLuong">
                                    <InputNumber
                                        min={0}
                                        precision={0}
                                        placeholder="Nhập tổng quỹ lương"
                                        style={{ width: '100%' }}
                                        formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <div style={{ fontWeight: 600, marginBottom: 8 }}>Quỹ lương từng tháng (đồng)</div>
                        <Row gutter={8}>
                            {THANG_LABELS.map((label, idx) => (
                                <Col span={6} key={idx}>
                                    <Form.Item label={label} name={`quyLuongThang${idx + 1}`}>
                                        <InputNumber
                                            min={0}
                                            precision={0}
                                            placeholder="0"
                                            style={{ width: '100%' }}
                                            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                        />
                                    </Form.Item>
                                </Col>
                            ))}
                        </Row>

                        <Row gutter={8}>
                            <Col span={24}>
                                <Form.Item label="Ghi chú" name="ghiChu">
                                    <Input.TextArea rows={2} placeholder="Nhập ghi chú (nếu có)" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item>
                            <Button
                                type="primary"
                                className="float-right"
                                htmlType="submit"
                                icon={<SaveOutlined />}
                            >
                                Lưu
                            </Button>
                        </Form.Item>
                    </Form>
                </ModalTextForm>
            )}

            <ContentHeader title="Quản lý quỹ lương" />

            <Row className="form-group" gutter={8}>
                <Col span={24}>
                    <Button
                        className="hrm-btn hrm-btn-warning float-right"
                        icon={<PlusOutlined />}
                        onClick={onMoModalThem}
                    >
                        Thêm quỹ lương
                    </Button>
                </Col>
            </Row>

            <Row gutter={8}>
                <Col span={24}>
                    <div className="card card-primary">
                        <Table
                            locale={{ emptyText: 'Không có dữ liệu' }}
                            dataSource={(dataDs || []).map((value, index) => ({ key: index, ...value }))}
                            loading={loading}
                            expandable={{
                                expandedRowRender: (record: any) => <ChiTietThang record={record} />,
                                rowExpandable: () => true,
                            }}
                        >
                            <Column
                                title="STT"
                                width={60}
                                align="center"
                                render={(_v, _r, index) => (
                                    <div className="text-align-middle">{index + 1}</div>
                                )}
                            />
                            <Column
                                title="Năm"
                                key="nam"
                                width={100}
                                align="center"
                                render={(value: any) => (
                                    <div className="text-align-middle" style={{ fontWeight: 600, fontSize: 15 }}>
                                        {value?.nam}
                                    </div>
                                )}
                            />
                            <Column
                                title="Tổng quỹ lương năm"
                                key="tongQuyLuong"
                                align="right"
                                render={(value: any) => (
                                    <div className="text-align-middle" style={{ color: '#1677ff', fontWeight: 600 }}>
                                        {formatVND(value?.tongQuyLuong ?? 0)}
                                    </div>
                                )}
                            />
                            <Column
                                title="Quỹ lương bình quân / tháng"
                                key="binhQuan"
                                align="right"
                                render={(value: any) => {
                                    const total = value?.tongQuyLuong ?? 0;
                                    return (
                                        <div className="text-align-middle">
                                            {formatVND(Math.round(total / 12))}
                                        </div>
                                    );
                                }}
                            />
                            <Column
                                title="Ghi chú"
                                key="ghiChu"
                                render={(value: any) => (
                                    <div className="text-align-middle">{value?.ghiChu}</div>
                                )}
                            />
                            <Column
                                title=""
                                width={120}
                                key="action"
                                align="center"
                                render={(value: any) => (
                                    <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                                        <Button
                                            size="small"
                                            className="hrm-btn hrm-btn-sm btn-primary"
                                            icon={<EditOutlined />}
                                            onClick={() => onMoModalSua(value)}
                                        >
                                            Sửa
                                        </Button>
                                        <Popconfirm
                                            title="Xác nhận xóa quỹ lương này?"
                                            onConfirm={() => onXoa(value?.id)}
                                            okText="Xóa"
                                            cancelText="Hủy"
                                        >
                                            <Button
                                                size="small"
                                                danger
                                                icon={<DeleteOutlined />}
                                            >
                                                Xóa
                                            </Button>
                                        </Popconfirm>
                                    </div>
                                )}
                            />
                        </Table>
                    </div>
                </Col>
            </Row>
        </section>
    );
};

export default QuanLyQuyLuong;


/*const THANG_LABELS = [
    'Tháng 1', 'Tháng 2', 'Tháng 3', 'Tháng 4',
    'Tháng 5', 'Tháng 6', 'Tháng 7', 'Tháng 8',
    'Tháng 9', 'Tháng 10', 'Tháng 11', 'Tháng 12',
];

const QuanLyQuyLuong = () => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [dataDs, setDataDs] = useState<QuyLuongInfo[]>([]);
    const [openModal, setOpenModal] = useState(false);
    const [editItem, setEditItem] = useState<QuyLuongInfo | null>(null);

    const layDuLieu = async () => {
        setLoading(true);
        try {
            const rs = await layDanhSachQuyLuong();
            setDataDs(rs);
        } catch (err: any) {
            toast.error(err?.toString().replace('Error: ', ''));
        }
        setLoading(false);
    };

    useEffect(() => {
        layDuLieu();
    }, []);

    const onMoModalThem = () => {
        setEditItem(null);
        form.resetFields();
        form.setFieldsValue({ nam: new Date().getFullYear() });
        setOpenModal(true);
    };

    const onMoModalSua = (item: QuyLuongInfo) => {
        setEditItem(item);
        form.setFieldsValue({
            nam: item.nam,
            tongQuyLuong: item.tongQuyLuong,
            quyLuongThang1: item.quyLuongThang1,
            quyLuongThang2: item.quyLuongThang2,
            quyLuongThang3: item.quyLuongThang3,
            quyLuongThang4: item.quyLuongThang4,
            quyLuongThang5: item.quyLuongThang5,
            quyLuongThang6: item.quyLuongThang6,
            quyLuongThang7: item.quyLuongThang7,
            quyLuongThang8: item.quyLuongThang8,
            quyLuongThang9: item.quyLuongThang9,
            quyLuongThang10: item.quyLuongThang10,
            quyLuongThang11: item.quyLuongThang11,
            quyLuongThang12: item.quyLuongThang12,
            ghiChu: item.ghiChu,
        });
        setOpenModal(true);
    };

    const onFinishLuu = async (values: any) => {
        dispatch(showLoadingScreen());
        try {
            const request = {
                id: editItem?.id ?? 0,
                nam: Number(values.nam),
                tongQuyLuong: Number(values.tongQuyLuong ?? 0),
                quyLuongThang1: Number(values.quyLuongThang1 ?? 0),
                quyLuongThang2: Number(values.quyLuongThang2 ?? 0),
                quyLuongThang3: Number(values.quyLuongThang3 ?? 0),
                quyLuongThang4: Number(values.quyLuongThang4 ?? 0),
                quyLuongThang5: Number(values.quyLuongThang5 ?? 0),
                quyLuongThang6: Number(values.quyLuongThang6 ?? 0),
                quyLuongThang7: Number(values.quyLuongThang7 ?? 0),
                quyLuongThang8: Number(values.quyLuongThang8 ?? 0),
                quyLuongThang9: Number(values.quyLuongThang9 ?? 0),
                quyLuongThang10: Number(values.quyLuongThang10 ?? 0),
                quyLuongThang11: Number(values.quyLuongThang11 ?? 0),
                quyLuongThang12: Number(values.quyLuongThang12 ?? 0),
                ghiChu: values.ghiChu ?? '',
            };
            const rs = await luuQuyLuong(request);
            if (rs > 0) {
                toast.success('Lưu quỹ lương thành công!');
                setOpenModal(false);
                layDuLieu();
            } else {
                toast.error('Lưu quỹ lương thất bại!');
            }
        } catch (err: any) {
            toast.error(err?.toString().replace('Error: ', ''));
        }
        dispatch(hideLoadingScreen());
    };

    const onXoa = async (id: number) => {
        dispatch(showLoadingScreen());
        try {
            await xoaQuyLuong(id);
            toast.success('Xóa quỹ lương thành công!');
            layDuLieu();
        } catch (err: any) {
            toast.error(err?.toString().replace('Error: ', ''));
        }
        dispatch(hideLoadingScreen());
    };

    return (
        <section>
            {openModal && (
                <ModalTextForm
                    title={editItem ? `Sửa quỹ lương năm ${editItem.nam}` : 'Thêm quỹ lương'}
                    hideFooter
                    show={openModal}
                    handleClose={() => setOpenModal(false)}
                    size="lg"
                >
                    <Form
                        layout="vertical"
                        validateMessages={validateMessages}
                        onFinish={onFinishLuu}
                        form={form}
                    >
                        <Row gutter={8}>
                            <Col span={8}>
                                <Form.Item rules={[{ required: true }]} label="Năm" name="nam">
                                    <InputNumber
                                        min={2000}
                                        max={2100}
                                        precision={0}
                                        placeholder="Nhập năm"
                                        style={{ width: '100%' }}
                                        disabled={!!editItem}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={16}>
                                <Form.Item rules={[{ required: true }]} label="Tổng quỹ lương năm (đồng)" name="tongQuyLuong">
                                    <InputNumber
                                        min={0}
                                        precision={0}
                                        placeholder="Nhập tổng quỹ lương"
                                        style={{ width: '100%' }}
                                        formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <div style={{ fontWeight: 600, marginBottom: 8 }}>Quỹ lương từng tháng (đồng)</div>
                        <Row gutter={8}>
                            {THANG_LABELS.map((label, idx) => (
                                <Col span={6} key={idx}>
                                    <Form.Item label={label} name={`quyLuongThang${idx + 1}`}>
                                        <InputNumber
                                            min={0}
                                            precision={0}
                                            placeholder="0"
                                            style={{ width: '100%' }}
                                            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                        />
                                    </Form.Item>
                                </Col>
                            ))}
                        </Row>

                        <Row gutter={8}>
                            <Col span={24}>
                                <Form.Item label="Ghi chú" name="ghiChu">
                                    <Input.TextArea rows={2} placeholder="Nhập ghi chú (nếu có)" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item>
                            <Button
                                type="primary"
                                className="float-right"
                                htmlType="submit"
                                icon={<SaveOutlined />}
                            >
                                Lưu
                            </Button>
                        </Form.Item>
                    </Form>
                </ModalTextForm>
            )}

            <ContentHeader title="Quản lý quỹ lương" />

            <Row className="form-group" gutter={8}>
                <Col span={24}>
                    <Button
                        className="hrm-btn hrm-btn-warning float-right"
                        icon={<PlusOutlined />}
                        onClick={onMoModalThem}
                    >
                        Thêm quỹ lương
                    </Button>
                </Col>
            </Row>

            <Row gutter={8}>
                <Col span={24}>
                    <div className="card card-primary">
                        <Table
                            locale={{ emptyText: 'Không có dữ liệu' }}
                            dataSource={(dataDs || []).map((value, index) => ({ key: index, ...value }))}
                            loading={loading}
                            scroll={{ x: 1400 }}
                        >
                            <Column
                                title="STT"
                                width={60}
                                align="center"
                                render={(_v, _r, index) => (
                                    <div className="text-align-middle">{index + 1}</div>
                                )}
                            />
                            <Column
                                title="Năm"
                                key="nam"
                                width={80}
                                align="center"
                                render={(value: any) => (
                                    <div className="text-align-middle">{value?.nam}</div>
                                )}
                            />
                            <Column
                                title="Tổng quỹ lương"
                                key="tongQuyLuong"
                                align="right"
                                render={(value: any) => (
                                    <div className="text-align-middle">{formatVND(value?.tongQuyLuong ?? 0)}</div>
                                )}
                            />
                            {THANG_LABELS.map((label, idx) => (
                                <Column
                                    key={`thang${idx + 1}`}
                                    title={label}
                                    align="right"
                                    width={110}
                                    render={(value: any) => (
                                        <div className="text-align-middle">
                                            {formatVND(value?.[`quyLuongThang${idx + 1}`] ?? 0)}
                                        </div>
                                    )}
                                />
                            ))}
                            <Column
                                title="Ghi chú"
                                key="ghiChu"
                                render={(value: any) => (
                                    <div className="text-align-middle">{value?.ghiChu}</div>
                                )}
                            />
                            <Column
                                title=""
                                width={100}
                                key="action"
                                align="center"
                                fixed="right"
                                render={(value: any) => (
                                    <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                                        <Button
                                            size="small"
                                            className="hrm-btn hrm-btn-sm btn-primary"
                                            icon={<EditOutlined />}
                                            onClick={() => onMoModalSua(value)}
                                        >
                                            Sửa
                                        </Button>
                                        <Popconfirm
                                            title="Xác nhận xóa quỹ lương này?"
                                            onConfirm={() => onXoa(value?.id)}
                                            okText="Xóa"
                                            cancelText="Hủy"
                                        >
                                            <Button
                                                size="small"
                                                className="hrm-btn hrm-btn-sm btn-danger"
                                                icon={<DeleteOutlined />}
                                                danger
                                            >
                                                Xóa
                                            </Button>
                                        </Popconfirm>
                                    </div>
                                )}
                            />
                        </Table>
                    </div>
                </Col>
            </Row>
        </section>
    );
};

export default QuanLyQuyLuong;
*/